import { Coord, coordKey, COLS, ROWS, colIndex } from './grid.js'

/** All available theaters (GDD-v3-theaters.md §3) */
export type TheaterId = 'classic' | 'hormuz' | 'giuk' | 'south-china-sea'

/** Terrain object types per theater */
export type TerrainType = 'oil_platform' | 'iceberg' | 'island' | 'reef'

/** A single blocked terrain cell on the board */
export interface TerrainCell {
  coord: Coord
  type: TerrainType
}

/** Pure-data theater configuration (JSON-serializable, no runtime deps) */
export interface TheaterConfig {
  id: TheaterId
  name: string
  /** One-line description shown on the theater select card */
  description: string
  /** Location subtitle */
  location: string
  /** Terrain cells when terrain is enabled (empty for classic) */
  terrainCells: TerrainCell[]
  /** Token key for client-side palette lookup */
  palette: string
}

/**
 * Theater registry.
 * Terrain layouts are fixed per theater (GDD-v3 §4) and must be
 * symmetric/mirrored so neither board has a placement advantage.
 */
export const THEATER_CONFIGS: Record<TheaterId, TheaterConfig> = {
  classic: {
    id: 'classic',
    name: 'Classic',
    description: 'Open ocean — the original Salvo board.',
    location: 'Open Ocean',
    terrainCells: [],
    palette: 'classic',
  },
  hormuz: {
    id: 'hormuz',
    name: 'Strait of Hormuz',
    description: 'Oil platforms block 2 cells on each board.',
    location: 'Persian Gulf → Gulf of Oman',
    // ART_GUIDE_v3 §5.3 — C2 and D5, diagonally symmetric
    terrainCells: [
      { coord: { col: 'C', row: 2 }, type: 'oil_platform' },
      { coord: { col: 'D', row: 5 }, type: 'oil_platform' },
    ],
    palette: 'hormuz',
  },
  giuk: {
    id: 'giuk',
    name: 'Greenland',
    description: 'Icebergs block 3 cells on each board.',
    location: 'Greenland ice edge, North Atlantic',
    terrainCells: [
      { coord: { col: 'B', row: 2 }, type: 'iceberg' },
      { coord: { col: 'E', row: 5 }, type: 'iceberg' },
      { coord: { col: 'D', row: 3 }, type: 'iceberg' },
    ],
    palette: 'giuk',
  },
  'south-china-sea': {
    id: 'south-china-sea',
    name: 'South China Sea',
    description: 'Islands and reefs block 4 cells on each board.',
    location: 'Spratly Islands / Paracel region',
    terrainCells: [
      { coord: { col: 'B', row: 2 }, type: 'island' },
      { coord: { col: 'E', row: 2 }, type: 'reef' },
      { coord: { col: 'B', row: 5 }, type: 'reef' },
      { coord: { col: 'E', row: 5 }, type: 'island' },
    ],
    palette: 'south-china-sea',
  },
}

/** All theater ids in display order */
export const ALL_THEATER_IDS: TheaterId[] = ['classic', 'hormuz', 'giuk', 'south-china-sea']

/**
 * Resolve the set of blocked cell keys for a game.
 * Returns an empty set when terrain is disabled or the theater has no terrain.
 */
export function getBlockedCells(theaterId: TheaterId, terrainEnabled: boolean): Set<string> {
  if (!terrainEnabled) return new Set()
  return new Set(THEATER_CONFIGS[theaterId].terrainCells.map(t => coordKey(t.coord)))
}

/** Blocked cell keys for an explicit terrain layout */
export function blockedCellsFromLayout(layout: TerrainCell[]): Set<string> {
  return new Set(layout.map(t => coordKey(t.coord)))
}

/**
 * Generate a randomized terrain layout for a theater.
 * Same terrain count and types as the static config (fairness: both boards
 * get an equal amount of blocked water), but cell positions vary per game
 * and per board. Cells are kept non-adjacent (orthogonally and diagonally)
 * and off the outer ring so ship placement always stays solvable and the
 * layout reads like the hand-tuned originals.
 */
export function generateTerrainLayout(
  theaterId: TheaterId,
  random: () => number = Math.random,
): TerrainCell[] {
  const types = THEATER_CONFIGS[theaterId].terrainCells.map(t => t.type)
  if (types.length === 0) return []

  const isNear = (a: Coord, b: Coord) =>
    Math.abs(colIndex(a.col) - colIndex(b.col)) <= 1 && Math.abs(a.row - b.row) <= 1

  // Retry whole layouts until the spacing constraint holds (tiny search space)
  for (let attempt = 0; attempt < 200; attempt++) {
    const cells: Coord[] = []
    while (cells.length < types.length) {
      // Inner 4×4 (cols B–E, rows 2–5), matching the static layouts
      const coord: Coord = {
        col: COLS[1 + Math.floor(random() * 4)],
        row: ROWS[1 + Math.floor(random() * 4)],
      }
      if (cells.some(c => isNear(c, coord))) break
      cells.push(coord)
    }
    if (cells.length === types.length) {
      return cells.map((coord, i) => ({ coord, type: types[i] }))
    }
  }

  // Fallback: the static layout (always valid)
  return THEATER_CONFIGS[theaterId].terrainCells
}
